import { useEffect, useRef } from 'react';
import { useGetUrlMetadataLazyQuery, GetUrlMetadataQuery } from '@graphql/generated';
import { isValidUrl } from '@shared/utils/urlHelpers';

type UrlMetadata = NonNullable<GetUrlMetadataQuery['urlMetadata']['metadata']>;

export type UseUrlMetadataUnfurlOptions = Readonly<{
  /** URL to fetch metadata for. Fetch triggers automatically when this changes to a new valid URL. */
  url?: string | null;
  /** Callback when metadata is successfully fetched */
  onSuccess?: (metadata: UrlMetadata) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}>;

type IdleState = Readonly<{
  status: 'idle';
}>;

type LoadingState = Readonly<{
  status: 'loading';
}>;

type ErrorState = Readonly<{
  status: 'error';
  /** Error message from the failed request */
  error: string;
}>;

type SuccessState = Readonly<{
  status: 'success';
  /** The fetched URL metadata */
  metadata: UrlMetadata;
  /** The final resolved URL (after redirects) */
  finalUrl: string | null;
}>;

export type UseUrlMetadataUnfurlResult = IdleState | LoadingState | ErrorState | SuccessState;

/**
 * Hook to fetch URL metadata (title, description, image, etc.).
 *
 * Automatically fetches metadata when the `url` option changes to a new valid URL.
 *
 * Returns a discriminated union based on status:
 * - `idle`: No URL provided or URL is invalid
 * - `loading`: Fetch in progress
 * - `error`: Fetch failed, includes `error` message
 * - `success`: Fetch succeeded, includes `metadata`, `finalUrl`
 *
 * @example
 * ```tsx
 * const [urlToFetch, setUrlToFetch] = useState<string | null>(null);
 *
 * const result = useUrlMetadataUnfurl({
 *   url: urlToFetch,
 *   onSuccess: (meta) => {
 *     if (meta.title) setTitle(meta.title);
 *   }
 * });
 *
 * // Check status for type-safe access to state-specific properties
 * if (result.status === 'success') {
 *   console.log(result.metadata.title);
 * }
 *
 * // Set the URL to trigger a fetch (e.g., on blur)
 * const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
 *   setUrlToFetch(e.target.value);
 * };
 * ```
 */
export function useUrlMetadataUnfurl(options: UseUrlMetadataUnfurlOptions = {}): UseUrlMetadataUnfurlResult {
  const { url, onSuccess, onError } = options;

  // Track the last fetched URL to avoid duplicate requests
  const lastFetchedUrlRef = useRef<string | null>(null);

  const [executeQuery, { loading, data, error: apolloError }] = useGetUrlMetadataLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    onCompleted: responseData => {
      const response = responseData?.urlMetadata;

      if (response?.error) {
        onError?.(response.error.message);
        return;
      }

      if (response?.metadata) {
        onSuccess?.(response.metadata);
      }
    },
    onError: err => {
      onError?.(err.message);
    },
  });

  // Automatically fetch when URL changes to a new valid value
  useEffect(() => {
    if (!url || !isValidUrl(url)) {
      return;
    }

    // Skip if we already fetched this URL
    if (url === lastFetchedUrlRef.current) {
      return;
    }

    lastFetchedUrlRef.current = url;
    executeQuery({
      variables: {
        input: { url },
      },
    });
  }, [url, executeQuery]);

  // Extract data from response
  const response = data?.urlMetadata;
  const metadata = response?.metadata ?? null;
  const finalUrl = response?.finalUrl ?? null;

  // Build error message from response or Apollo error
  const errorMessage = response?.error?.message ?? apolloError?.message ?? null;

  if (loading) {
    return { status: 'loading' } as const;
  }

  if (errorMessage) {
    return { status: 'error', error: errorMessage } as const;
  }

  if (metadata) {
    return { status: 'success', metadata, finalUrl } as const;
  }

  return { status: 'idle' } as const;
}
